import type { KeyboardEvent } from 'react';
import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  addHours,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInMinutes,
  format,
  isValid,
  parseISO,
} from 'date-fns';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { type Country } from 'react-phone-number-input';

export function mergeClassNames(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Keyboard activation for role="button" divs; Space would otherwise scroll the page.
// Ignores events bubbling from focusable children.
export const activateOnKeyDown = (action: () => void) => (e: KeyboardEvent) => {
  if (e.target !== e.currentTarget) return;
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  action();
};

export const formatDateWithConditionalYear = (date: string | Date) => {
  const currentYear = new Date().getFullYear();
  const dateYear = new Date(date).getFullYear();

  if (dateYear === currentYear) {
    return format(new Date(date), 'MMM d');
  }

  return format(new Date(date), 'MMM d, yyyy');
};

// 2h buffer covers Stripe processing delays so the real charge lands before the displayed time.
export const addRenewalBuffer = (date: string | Date): Date =>
  addHours(new Date(date), 2);

export type TimeFormat = '12h' | '24h';

const timeZoneFormatters = new Map<string, Intl.DateTimeFormat>();

// A zone Intl rejects (empty or non-IANA) falls back to the device zone
// instead of throwing mid-render.
export const formatInTimeZone = (
  date: Date,
  timeZone: string | undefined,
  options: Intl.DateTimeFormatOptions,
  locale = 'en-US'
) => {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`;
  let formatter = timeZoneFormatters.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    } catch {
      formatter = new Intl.DateTimeFormat(locale, options);
    }
    timeZoneFormatters.set(key, formatter);
  }
  return formatter.format(date);
};

const clockTimeOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

// The 12-hour string is assembled by hand: browsers' ICU puts U+202F before
// AM/PM, which the admin and the emails do not emit, so the same time would
// look different across the three surfaces.
export const splitClockTime = (
  date: string | number | Date,
  timeFormat: TimeFormat,
  timeZone?: string
): { clock: string; period: 'AM' | 'PM' | null } => {
  const clock = formatInTimeZone(new Date(date), timeZone, clockTimeOptions);
  if (timeFormat === '24h') return { clock, period: null };
  const [hours, minutes] = clock.split(':');
  const hour = Number(hours);
  return {
    clock: `${hour % 12 || 12}:${minutes}`,
    period: hour >= 12 ? 'PM' : 'AM',
  };
};

export const formatClockTime = (
  date: string | number | Date,
  timeFormat: TimeFormat,
  timeZone?: string
): string => {
  const { clock, period } = splitClockTime(date, timeFormat, timeZone);
  return period ? `${clock} ${period}` : clock;
};

// "Sep 9", "Tue, Sep 9", "Tuesday, Sep 9, 2027": the year appears only when the
// date's year (in the zone shown) differs from today's.
export const formatDateInTimeZone = (
  date: Date,
  timeZone: string | undefined,
  weekday?: 'short' | 'long'
): string => {
  const showYear =
    formatInTimeZone(date, timeZone, { year: 'numeric' }) !==
    formatInTimeZone(new Date(), timeZone, { year: 'numeric' });
  return formatInTimeZone(date, timeZone, {
    weekday,
    month: 'short',
    day: 'numeric',
    year: showYear ? 'numeric' : undefined,
  });
};

// A day, not an instant: noon UTC printed in UTC is that day everywhere.
export const formatDayKey = (dayKey: string): string =>
  formatDateInTimeZone(new Date(`${dayKey}T12:00:00Z`), 'UTC', 'long');

// The one "<date> at <time>" recipe for a class occurrence. Class times show in
// the visitor's device zone on every page; the emails carry studio time.
// Callers pass the device zone, mostly as the availability read echoed it back.
export const formatClassDateTime = (
  date: string | Date,
  timeFormat: TimeFormat,
  timeZone: string | undefined,
  weekday?: 'short' | 'long'
): string => {
  const instant = new Date(date);
  return `${formatDateInTimeZone(instant, timeZone, weekday)} at ${formatClockTime(instant, timeFormat, timeZone)}`;
};

const calendarDayOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

// en-CA renders YYYY-MM-DD, parseable as a UTC day for calendar-day math.
export const toZonedDateKey = (date: Date, timeZone: string | undefined) =>
  formatInTimeZone(date, timeZone, calendarDayOptions, 'en-CA');

export const daysBetweenDateKeys = (from: string, to: string): number =>
  Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) /
      86_400_000
  );

const calendarDaysFromNow = (date: Date, now: Date): number =>
  daysBetweenDateKeys(
    toZonedDateKey(now, undefined),
    toZonedDateKey(date, undefined)
  );

export const formatHourCount = (hours: number): string =>
  `${hours} ${hours === 1 ? 'hour' : 'hours'}`;

export const getStartProximityLabel = (
  startTime: string | Date,
  endTime: string | Date,
  options?: { now?: Date; includeDays?: boolean }
): { label: string; color: 'green' | 'orange' } | null => {
  const { now = new Date(), includeDays = false } = options ?? {};
  const start = new Date(startTime);
  const end = new Date(endTime).getTime();
  const ts = now.getTime();
  const diffMs = start.getTime() - ts;

  if (diffMs <= 0) {
    return ts <= end ? { label: 'Happening now!', color: 'orange' } : null;
  }

  const ONE_MIN = 60_000;
  const ONE_HOUR = 60 * ONE_MIN;

  if (diffMs < ONE_MIN) return { label: 'Starting now', color: 'green' };

  if (diffMs < ONE_HOUR) {
    const minutes = Math.floor(diffMs / ONE_MIN);
    return {
      label: `In ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`,
      color: 'green',
    };
  }

  // Full 24h window guarantees the calendar-day branch below only ever sees
  // day diffs >= 1.
  if (diffMs < 24 * ONE_HOUR) {
    const hours = Math.floor(diffMs / ONE_HOUR);
    return {
      label: `In ${formatHourCount(hours)}`,
      color: 'green',
    };
  }

  const diffDays = calendarDaysFromNow(start, now);
  if (diffDays === 1) return { label: 'Tomorrow', color: 'green' };
  if (includeDays && diffDays >= 2 && diffDays <= 6) {
    return { label: `In ${diffDays} days`, color: 'green' };
  }
  return null;
};

// The currencies whose symbol goes after the amount, behind a no-break space:
// "449 kr", "1200 zł", "990 Kč", "5000 Ft", "120 lei". Every other symbol goes
// in front ("€25", "$25", "CHF 25"), the euro too in countries that write it
// after, which is a product decision. Hand-copied in the backend's
// src/utils/currency.ts and the admin app's src/lib/currencies.ts: change all
// three, or the booking page, the admin and the emails place one price two
// ways.
const SUFFIX_SYMBOL_CURRENCIES = new Set([
  'SEK',
  'NOK',
  'DKK',
  'ISK',
  'PLN',
  'CZK',
  'HUF',
  'RON',
]);

// Hand-copied in the admin app's src/lib/utils.ts, which previews prices
// with it: change both, or a studio's preview and its booking page show two
// different symbols and nothing fails.
//
// Derived from Intl so every currency a studio can pick renders its own
// symbol. The result keeps the separator ICU puts after a letter symbol
// ("CHF 45"), which is how it reads in front of an amount; formatMoney decides
// where it goes. An absent code renders no symbol rather than a guessed one:
// every priced row the backend sends is stamped with the studio currency, so
// this only shows before data arrives.
const currencySymbols = new Map<string, string>();

const getCurrencySymbol = (currencyCode: string | null | undefined): string => {
  if (!currencyCode) return '';
  const code = currencyCode.toUpperCase();
  const cached = currencySymbols.get(code);
  if (cached) return cached;
  const parts = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: code,
    currencyDisplay: 'narrowSymbol',
  }).formatToParts(0);
  const at = parts.findIndex((part) => part.type === 'currency');
  const next = parts[at + 1];
  const symbol = parts[at].value + (next?.type === 'literal' ? next.value : '');
  currencySymbols.set(code, symbol);
  return symbol;
};

const FREE_PRICE_LABEL = 'Free';

// The one place a currency symbol meets an amount. The cents are rounded the
// way a charge is, so 19.9 from the API reads 19.90 on the catalogue and the
// receipt alike: a whole amount prints no decimals and any cents print two. A
// negative amount that does not round to zero opens with a real minus
// (U+2212), and the symbol goes after the amount for the suffix currencies
// ("449 kr") and in front for every other ("€25", "CHF 25").
export const formatMoney = (
  amount: number,
  currencyCode: string | null | undefined
): string => {
  const cents = Math.round(Math.abs(amount) * 100);
  const value = (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  const sign = amount < 0 && cents > 0 ? '\u2212' : '';
  const symbol = getCurrencySymbol(currencyCode);
  return currencyCode &&
    SUFFIX_SYMBOL_CURRENCIES.has(currencyCode.toUpperCase())
    ? `${sign}${value}\u00A0${symbol.trim()}`
    : `${sign}${symbol}${value}`;
};

// A catalogue price only: an empty one is how a studio marks a product free —
// the admin form stores null and refuses 0 — so an absent price is a label,
// not a gap. For money that was actually charged, use formatAmount instead;
// there 0 means zero was charged, which is not the same claim as "Free".
export const formatPrice = (
  price: number | null | undefined,
  currencyCode: string | null | undefined
): string =>
  !price || price <= 0 ? FREE_PRICE_LABEL : formatMoney(price, currencyCode);

export const formatAmount = (
  amount: number | null | undefined,
  currencyCode: string | null | undefined
): string | null => (amount == null ? null : formatMoney(amount, currencyCode));

export const formatCycles = (cycles: number): string =>
  cycles === 1 ? '1 payment' : `${cycles} payments`;

export const formatDurationBetween = (
  startTime: Date | string,
  endTime: Date | string
): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  return convertMinutesToTime(differenceInMinutes(end, start));
};

// Past an hour, print h:mm:ss rather than a three- or four-digit minute count.
export const formatCountdown = (msLeft: number): string => {
  const totalSeconds = Math.max(0, Math.floor(msLeft / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const tail = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${tail}` : tail;
};

export const getReceiptDateParts = (
  start: Date,
  end: Date,
  timeFormat: TimeFormat
): { dateFull: string; dateShort: string; time: string; duration: string } => {
  return {
    dateFull: formatDateInTimeZone(start, undefined, 'long'),
    dateShort: formatDateInTimeZone(start, undefined, 'short'),
    time: formatClockTime(start, timeFormat),
    duration: `(${formatDurationBetween(start, end)})`,
  };
};

const isKnownTimeZone = (timeZone: string) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
};

// A browser that cannot read the device zone reports Etc/Unknown, which the API
// refuses, or no zone at all, and then runs on UTC itself. UTC keeps the API's
// day buckets in line with the times the page shows.
export const getUserTimezone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timeZone && isKnownTimeZone(timeZone) ? timeZone : 'UTC';
};

// The API takes four-digit years only, and a month's availability request runs
// a week past the month's end, so a December 9999 link would already ask for
// the year 10000.
const LAST_LINKABLE_YEAR = 9998;

// A link's yyyy-MM-dd is a day on the visitor's own calendar. new Date() reads
// that shape as UTC midnight, the evening before anywhere west of UTC, so a US
// link landed a day early and a link for today was dropped as past. parseISO
// alone would also take a bare year, a week date or a full timestamp, so the
// shape is checked first.
const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

export const parseDateParam = (value: string): Date | null => {
  if (!DATE_KEY.test(value)) return null;
  const date = parseISO(value);
  return isValid(date) && date.getFullYear() <= LAST_LINKABLE_YEAR
    ? date
    : null;
};

export const formatMaskedPhone = (masked: string): string =>
  masked.replace(/[*-]{2,}/g, '***').replace(/\d(\d{3})$/, '$1');

export const getFullName = (firstName: string, lastName?: string | null) => {
  return `${firstName ?? ''} ${lastName ?? ''}`.trim();
};

const capitalizeWord = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1);

const capitalizeName = (name: string) =>
  name.split(' ').map(capitalizeWord).join(' ');

export const buildInstructorName = (
  first?: string | null,
  last?: string | null
) => capitalizeName(getFullName(first ?? '', last));

export const getAbbreviatedName = (
  firstName: string | null | undefined,
  lastName?: string | null
) => {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();

  if (!first && !last) return '';
  if (first && !last) return first;
  if (!first && last) return last;

  const capFirst =
    first === first.toLowerCase() ? capitalizeWord(first) : first;
  const lastInitial = last.charAt(0).toUpperCase();
  return `${capFirst} ${lastInitial}`;
};

export const classWithInstructorText = (
  className: string,
  instructorName: string | null | undefined
): string =>
  instructorName ? `${className} with ${instructorName}` : className;

interface CalendarEvent {
  title: string;
  description?: string;
  location?: string;
  startTime: Date;
  endTime: Date;
}

const formatCalendarDateUtc = (date: Date) =>
  date.toISOString().replace(/-|:|\.\d+/g, '');

export const generateGoogleCalendarUrl = (event: CalendarEvent): string => {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatCalendarDateUtc(event.startTime)}/${formatCalendarDateUtc(event.endTime)}`,
  });

  if (event.description) {
    params.append('details', event.description);
  }

  if (event.location) {
    params.append('location', event.location);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// RFC 5545: a raw newline or comma would terminate the value and corrupt the file.
const escapeIcsText = (text: string) =>
  text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');

export const generateICalFile = (event: CalendarEvent): string => {
  // Deterministic UID so a re-downloaded event updates instead of duplicating.
  const uid = `${formatCalendarDateUtc(event.startTime)}-${event.title.replace(/[^A-Za-z0-9]/g, '')}@mokoboko`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Mokoboko//Class Booking//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatCalendarDateUtc(new Date())}`,
    `DTSTART:${formatCalendarDateUtc(event.startTime)}`,
    `DTEND:${formatCalendarDateUtc(event.endTime)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    event.description ? `DESCRIPTION:${escapeIcsText(event.description)}` : '',
    event.location ? `LOCATION:${escapeIcsText(event.location)}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
};

export const downloadICalFile = (event: CalendarEvent): void => {
  const icsContent = generateICalFile(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', `${event.title}.ics`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface DirectionsDestination {
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
}

export const generateGoogleMapsDirectionsUrl = (
  destination: DirectionsDestination
): string | null => {
  const hasCoordinates =
    destination.latitude != null && destination.longitude != null;
  // Maps URLs API requires a destination text even alongside a place_id;
  // the place_id wins for the actual pin.
  const destinationText = hasCoordinates
    ? `${destination.latitude},${destination.longitude}`
    : destination.address;
  if (!destinationText) return null;

  const params = new URLSearchParams({
    api: '1',
    destination: destinationText,
  });
  if (destination.placeId) {
    params.append('destination_place_id', destination.placeId);
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

// One shape wherever a class length is printed: "50 min", "60 min",
// "1h 15 min", "2h". The hour reads in minutes, as class lengths are compared.
export const convertMinutesToTime = (totalMinutes: number): string => {
  if (totalMinutes === 60) return '60 min';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || hours === 0) parts.push(`${minutes} min`);

  return parts.join(' ');
};

export const toDialCode = (iso: string): string =>
  `+${getCountryCallingCode(iso as Country)}`;

const normalizeCallingCode = (code: string): string => {
  const digits = (code || '').replace(/[^0-9]/g, '');
  return digits.replace(/^0+/, '');
};

let _callingCodeToIsoList: Map<string, string[]> | null = null;

const getCallingCodeToIsoList = (): Map<string, string[]> => {
  if (!_callingCodeToIsoList) {
    _callingCodeToIsoList = new Map<string, string[]>();
    for (const iso of getCountries()) {
      const code = getCountryCallingCode(iso);
      if (!_callingCodeToIsoList.has(code)) {
        _callingCodeToIsoList.set(code, []);
      }
      _callingCodeToIsoList.get(code)!.push(iso);
    }
  }
  return _callingCodeToIsoList;
};

export const getIsosFromCallingCode = (code: string): string[] => {
  const normalized = normalizeCallingCode(code);
  return getCallingCodeToIsoList().get(normalized) ?? [];
};

export const getIsosFromCallingCodeFiltered = (
  code: string,
  allowedCountries: string[]
): string[] => {
  return getIsosFromCallingCode(code).filter((iso) =>
    allowedCountries.includes(iso)
  );
};

export const getIsoFromCallingCode = (
  code: string | null,
  preferred?: string[]
): string => {
  const fallbackIso = 'PT';
  if (!code) {
    return fallbackIso;
  }

  if (/^[A-Za-z]{2}$/.test(code)) {
    return code.toUpperCase();
  }

  const candidates = getIsosFromCallingCode(code);
  if (!candidates.length) {
    return fallbackIso;
  }

  if (preferred && preferred.length) {
    const hit = preferred.find((p) => candidates.includes(p));
    if (hit) return hit;
  }

  return candidates[0] || fallbackIso;
};

// Drops the national trunk prefix (e.g. RO/GB leading 0) while preserving
// digits that are significant for the country (e.g. Italian leading 0).
export const toNationalPhone = (phone: string, country: Country): string => {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  const parsed = parsePhoneNumberFromString(digits, country);
  return parsed?.isValid() ? parsed.nationalNumber : digits;
};

export const formatPhoneForDisplay = (
  phone: string,
  country: Country
): string => {
  const digits = (phone || '').replace(/\D/g, '');
  const parsed = parsePhoneNumberFromString(digits, country);
  return parsed?.isValid()
    ? parsed.formatInternational()
    : `${toDialCode(country)} ${digits}`;
};

export type ChargeEveryInterval =
  | 'WEEK'
  | 'MONTH'
  | 'QUARTER'
  | 'SEMI_ANNUAL'
  | 'YEAR';

export const calculateNextPaymentDate = (
  count: number,
  interval: ChargeEveryInterval | string
): Date => {
  const today = new Date();
  switch (interval.toUpperCase()) {
    case 'WEEK':
      return addWeeks(today, count);
    case 'MONTH':
      return addMonths(today, count);
    case 'QUARTER':
      return addQuarters(today, count);
    case 'SEMI_ANNUAL':
      return addMonths(today, count * 6);
    case 'YEAR':
      return addYears(today, count);
    default:
      return addMonths(today, count);
  }
};

export const formatInterval = (
  count: number,
  interval: ChargeEveryInterval | string,
  options?: { shorten?: boolean; withSpace?: boolean }
): string => {
  const { shorten = true, withSpace = false } = options ?? {};
  const labelMap: Record<string, string> = {
    WEEK: 'week',
    MONTH: shorten ? 'mo' : 'month',
    QUARTER: 'quarter',
    SEMI_ANNUAL: 'semi-annual',
    YEAR: 'year',
  };
  const base =
    labelMap[interval.toUpperCase()] ??
    interval.toLowerCase().replace('_', ' ');
  const unit = count === 1 ? base : `${base}s`;
  const prefix = withSpace ? ' / ' : '/';
  return count === 1 ? `${prefix}${unit}` : `${prefix}every ${count} ${unit}`;
};

export const formatRecurringPrice = (
  price: number | null | undefined,
  currencyCode: string | null | undefined,
  count: number,
  interval: ChargeEveryInterval | string | null | undefined,
  options?: { shorten?: boolean }
): string => {
  const label = formatPrice(price, currencyCode);
  // "Free" already says there is no recurring charge, so "Free/mo" reads wrong.
  // Tested against the label so the free rule stays owned by formatPrice alone.
  if (label === FREE_PRICE_LABEL || !interval) return label;
  return `${label}${formatInterval(count, interval, options)}`;
};

export const formatBillingFrequency = (
  count: number,
  interval: ChargeEveryInterval | string
): string => {
  const base = interval.toLowerCase().replace('_', ' ');
  if (count === 1) {
    return base === 'month' ? 'monthly' : base === 'week' ? 'weekly' : base;
  }
  return `every ${count} ${base}${count > 1 ? 's' : ''}`;
};

const PERIOD_MONTHS: Record<string, number> = { QUARTER: 3, SEMI_ANNUAL: 6 };

// How long one billing period lasts: "2 weeks", "3 months", "1 year".
export const formatPeriodLength = (
  count: number,
  interval: ChargeEveryInterval | string
): string => {
  const key = interval.toUpperCase();
  const [length, unit] = PERIOD_MONTHS[key]
    ? [count * PERIOD_MONTHS[key], 'month']
    : [count, getIntervalLabel(key)];
  return `${length} ${unit}${length === 1 ? '' : 's'}`;
};

// "every month", or "every 3 months" for a plan billed every three.
export const formatIntervalForTerms = (
  count: number,
  interval: ChargeEveryInterval | string
): string => {
  const label = getIntervalLabel(interval);
  return `every ${count === 1 ? label : `${count} ${label}s`}`;
};

// payToday and planPrice differ under a full discount: a 100%-off coupon means
// nothing is due now but the plan still renews, while a plan with no catalogue
// price never charges again — so the next-payment promise belongs only to the
// second case being false.
// paidByGiftCard: a money gift card pays toward the first payment (gift cards
// D306), so a first payment it covers whole is paid, never "No payment".
export const getSubscriptionPaymentInfo = (
  payToday: number,
  planPrice: number | null | undefined,
  currencyCode: string | null | undefined,
  count: number,
  interval: ChargeEveryInterval | string,
  paidByGiftCard = false
): string => {
  const todayText =
    payToday > 0
      ? `Pay ${formatMoney(payToday, currencyCode)} today.`
      : paidByGiftCard
        ? 'Your gift card pays today.'
        : 'No payment today.';

  if (!planPrice) return `${todayText} Free forever, cancel anytime.`;

  const nextDate = calculateNextPaymentDate(count, interval);
  const frequency = formatIntervalForTerms(count, interval);

  return `${todayText} Next payment will be on ${format(
    nextDate,
    'MMM d, yyyy'
  )}. Billed ${frequency}, pause or cancel anytime.`;
};

export const getIntervalLabel = (interval: string): string => {
  const labelMap: Record<string, string> = {
    WEEK: 'week',
    MONTH: 'month',
    QUARTER: 'quarter',
    SEMI_ANNUAL: 'semi-annual period',
    YEAR: 'year',
  };
  return labelMap[interval] ?? 'billing period';
};

export const getIntervalAdjective = (interval: string): string => {
  const adjectiveMap: Record<string, string> = {
    WEEK: 'Weekly',
    MONTH: 'Monthly',
    QUARTER: 'Quarterly',
    SEMI_ANNUAL: 'Semi-annual',
    YEAR: 'Yearly',
  };
  return adjectiveMap[interval] ?? 'Recurring';
};

// The largest unit that divides the days exactly, a year counted as 365 and a
// month as 30.
export const formatDays = (
  days: number,
  { short = false }: { short?: boolean } = {}
): string => {
  const [count, unit] =
    days % 365 === 0
      ? [days / 365, 'year']
      : days % 30 === 0
        ? [days / 30, 'month']
        : days % 7 === 0
          ? [days / 7, 'week']
          : [days, 'day'];
  if (short && unit === 'month') return `${count} mon`;
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
};

// Resolve a post-login redirect param, rejecting any target that resolves
// off-origin. Parsing against the real origin catches protocol-relative,
// backslash and control-char tricks the browser normalizes to //host.
// A credential the head script in app/layout.tsx moved from the URL into
// sessionStorage. The URL still holds it when the script could not save it.
export const readUrlCredential = (
  param: string,
  storageKey: string
): string | null => {
  const fromUrl = new URLSearchParams(window.location.search).get(param);
  if (fromUrl) return fromUrl;
  try {
    return sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

export const resolveRedirectPath = (
  redirect: string | null | undefined,
  fallback = '/dashboard'
): string => {
  if (!redirect || typeof window === 'undefined') return fallback;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
};

export const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const httpsUrl = (value: string | null | undefined) =>
  value?.startsWith('https://') ? value : null;
